import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useInvestorChangeRequests } from '@/hooks/react-query/useChangeRequests';
import { formatDate } from '@/utils/formatters';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { ChangeRequestReviewDialog } from '@/components/change-requests/ChangeRequestReviewDialog';
import type { ChangeRequestSummary } from '@/types/changeRequest';

interface InvestorChangeRequestsProps {
  investorId: string;
}

export function InvestorChangeRequests({
  investorId,
}: InvestorChangeRequestsProps) {
  const { data, isLoading, error } = useInvestorChangeRequests(investorId);
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        icon: React.ReactNode;
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
      }
    > = {
      pending: {
        icon: <Clock className="mr-1 h-3 w-3" />,
        variant: 'outline',
      },
      approved: {
        icon: <CheckCircle className="mr-1 h-3 w-3" />,
        variant: 'default',
      },
      rejected: {
        icon: <XCircle className="mr-1 h-3 w-3" />,
        variant: 'destructive',
      },
      cancelled: {
        icon: <XCircle className="mr-1 h-3 w-3" />,
        variant: 'secondary',
      },
    };

    const config = statusConfig[status] || {
      icon: null,
      variant: 'outline' as const,
    };

    return (
      <Badge variant={config.variant} className="flex items-center w-fit">
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getRequestTypeBadge = (type: string) => {
    const label = type
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
    return <Badge variant="secondary">{label}</Badge>;
  };

  const pendingCount =
    data?.items?.filter((r) => r.status === 'pending').length || 0;
  const approvedCount =
    data?.items?.filter((r) => r.status === 'approved').length || 0;
  const rejectedCount =
    data?.items?.filter((r) => r.status === 'rejected').length || 0;

  return (
    <div className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>About Change Data</AlertTitle>
        <AlertDescription>
          Investor contacts can request changes to sensitive information like
          banking details and addresses. These requests require administrator
          approval before being applied.
        </AlertDescription>
      </Alert>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
            <p className="text-xs text-muted-foreground">Changes applied</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">Not approved</p>
          </CardContent>
        </Card>
      </div>

      {/* Change Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Change Data History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataLoader
            isLoading={isLoading}
            error={error}
            data={data?.items}
            emptyStateMessage="No change data for this investor"
            showEmptyState={(items) => !items || items.length === 0}
          >
            {(items: ChangeRequestSummary[]) => (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Requested At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((request: ChangeRequestSummary) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        {getRequestTypeBadge(request.request_type)}
                      </TableCell>
                      <TableCell>{request.requested_by_contact_name}</TableCell>
                      <TableCell>{formatDate(request.requested_at)}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-right">
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => setSelectedRequest(request.id)}
                          >
                            Review
                          </Button>
                        )}
                        {request.status !== 'pending' && (
                          <span className="text-sm text-muted-foreground">
                            {request.status === 'approved' && 'Applied'}
                            {request.status === 'rejected' && 'Declined'}
                            {request.status === 'cancelled' && 'Cancelled'}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataLoader>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      {selectedRequest && (
        <ChangeRequestReviewDialog
          requestId={selectedRequest}
          isOpen={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
}
